import type { StationId } from '@/domain/types';
import type { EventLogEntry, EventLogRepository } from '@/persistence/ports';
import type { Evse3Database, EventLogRow } from '../db';

/**
 * Persists OCPP frames with full fidelity — messageType, messageId, action,
 * payload all round-trip so the Event Log tab survives a tab reload (FEAT-006).
 */
export class EventLogRepositoryDexie implements EventLogRepository {
  constructor(private readonly db: Evse3Database) {}

  async append(entry: EventLogEntry): Promise<void> {
    const row: EventLogRow = {
      stationId: entry.stationId,
      seq: entry.seq,
      createdAt: entry.createdAt,
      direction: entry.direction === 'out' ? 'SEND' : 'RECV',
      messageType: entry.messageType,
      messageId: entry.messageId,
      ...(entry.action !== null ? { action: entry.action } : {}),
      payload: entry.payload,
    };
    await this.db.eventLog.put(row);
  }

  async list(
    stationId: StationId,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<readonly EventLogEntry[]> {
    let coll = this.db.eventLog.where('stationId').equals(stationId).reverse();
    if (opts.offset !== undefined && opts.offset > 0) coll = coll.offset(opts.offset);
    if (opts.limit !== undefined && opts.limit >= 0) coll = coll.limit(opts.limit);
    const rows = await coll.toArray();
    // Reverse the descending DB ordering so callers get the entries oldest-
    // first — matches the in-memory ring buffer's natural order.
    return rows.reverse().map(rowToEntry);
  }

  async clear(stationId: StationId): Promise<void> {
    await this.db.eventLog.where('stationId').equals(stationId).delete();
  }
}

function rowToEntry(row: EventLogRow): EventLogEntry {
  return {
    stationId: row.stationId,
    seq: row.seq,
    action: row.action ?? null,
    direction: row.direction === 'SEND' ? 'out' : 'in',
    messageType: row.messageType,
    messageId: row.messageId,
    payload: row.payload,
    createdAt: row.createdAt,
  };
}
